import { useEffect, useState, useContext } from 'react';
import { t } from 'i18next';
import {
  useMedicalClaimDetails,
  useMedicalClaimEOB,
} from '@sentara-health/sentara-api-hooks-core';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  convertStringToDate,
  convertAPIRequestFormat,
  UserDetails,
  apiResponseSafeCheck,
  getCache,
  errorhandler,
  NavigateConst,
  RightArrowIcon,
  safeJsonParse,
} from 'common';
import ApiWrapper from '../../common/ApiWrapper';
import jsPDF from 'jspdf';
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';
import { GlobalContext } from 'context';
import {
  Table,
  PrintIcon,
  Headings,
  ButtonLink,
  DownloadIcon,
  ContactInfo,
  Loader,
  Link,
  Button,
  Paragraph,
} from '@sentara-health/optimahealth_web';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

const MedicalClaimsDetails = () => {
  const location = useLocation(); /**use location api to get location */
  const navigate = useNavigate(); /**use navigate to navigate */
  const darkgraycolor = '#333131';
  const { serviceNumber, getFeature } = useContext(GlobalContext);

  const { MedicalClaimEOBType } = useMedicalClaimEOB();
  const [getMedicalDetails, setMedicalDetails] = useState<any>();
  const [getMedicalService, setMedicalService] = useState<any>();
  const [getPhone, setPhone] = useState<any>({});
  const [showNoContent, setShowNoContent] = useState(false);
  const [showData, setShowData] = useState(false);
  const { MedicalClaimDetailsType } =
    useMedicalClaimDetails(); /**medical claim details API */
  const [loader, setLoader] = useState(false);
  const { data, apiError, callApi } = ApiWrapper(MedicalClaimDetailsType); // API call for Claims Details

  // To pass the default date if is is EOBSummary
  const currentDate = new Date();
  currentDate.setMonth(currentDate.getMonth() - 18);
  const fromdate = convertAPIRequestFormat(currentDate);
  const todate = convertAPIRequestFormat(new Date());
  const routeParams = useParams();
  useEffect(() => {
    setLoader(true);
    setShowData(true);
    (async () => {
      let data;
      data = {
        MemberId:
          location?.state?.memberId ?? localStorage.getItem(UserDetails.mId),
        ClaimType: location?.state?.ClaimType ?? routeParams?.claimType,
        endDate: location?.state?.endDate
          ? convertAPIRequestFormat(location.state.endDate)
          : todate,
        startDate: location?.state?.startDate
          ? convertAPIRequestFormat(location.state.startDate)
          : fromdate,
      };
      let claimIdData;
      // The EOB palyload is different from the claims summary payload
      if (routeParams?.isToken === 'true') {
        claimIdData = {
          claimTokenId: routeParams?.claimToken,
          memberSuffix: routeParams?.memberSuffix,
        };
      } else {
        claimIdData = {
          ClaimId: location?.state?.ClaimId,
        };
      }
      data = { ...data, ...claimIdData };

      callApi(data, getCache());
      setPhone(safeJsonParse(serviceNumber, {}));
    })();
  }, []);

  useEffect(() => {
    setShowData(false);
    // To check the data is not empty and the response is success
    if (data && apiResponseSafeCheck(data)) {
      const { claimDetails, typeOfService } = data;
      setMedicalDetails(claimDetails);
      setMedicalService(typeOfService);
    } else if (data === '' || data === undefined) {
      setShowNoContent(true);
    }
    // To remove the loader when the API response is success or error
    if (apiError || data || data === '') {
      setLoader(false);
    }
    localStorage.setItem(UserDetails.memberSuffix, '');
  }, [data, apiError]);

  const Print = (e: any) => {
    e.preventDefault();
    window.print();
  };

  // Generate PDF for EOB based on claim data
  const eobPDF = async (claimID: string) => {
    setLoader(true);
    const postData = {
      claimID: claimID,
      claimType: location?.state?.ClaimType || routeParams.claimType,
      memberId:
        location?.state?.memberId || localStorage.getItem(UserDetails.mId),
    };
    const response = await MedicalClaimEOBType(postData, getCache());
    if (typeof response?.data === 'string') {
      pdfConverter(response.data, claimID);
    } else {
      errorhandler(response?.errorCode);
    }
    setLoader(false);
  };

  // Function to generate PDF from data
  const pdfConverter = (data: string, claimID: string) => {
    const filename = claimID;
    const binary = atob(data?.replace(/([^\r])\n/g, '$1\r\n'));
    const len = binary.length;
    const buffer = new ArrayBuffer(len);
    const view = new Uint8Array(buffer);
    for (let i = 0; i < len; i++) {
      view[i] = binary.charCodeAt(i);
    } // create the blob object with content-type "application/pdf"
    const blob = new Blob([view], { type: 'application/pdf' });
    saveAs(blob, filename);
  };

  //navigate back to medical claims page when click on back button
  const backToMedicalClaims = () => {
    if (routeParams?.isToken === 'true') {
      navigate(NavigateConst.medicalClaimsURL);
    } else {
      navigate(-1);
    }
  };

  /* download PDF function to download PDF document */
  const DownloadPDF = (e: any) => {
    e.preventDefault();
    const capture = document.querySelector('.print-table') as HTMLElement;
    html2canvas(capture).then((canvas: HTMLCanvasElement) => {
      const imgData = canvas.toDataURL('image/png');
      const doc = new jsPDF();
      const componentWidth = doc.internal.pageSize.getWidth();
      const componentHeight = doc.internal.pageSize.getHeight();
      doc.addImage(imgData, 'PNG', 0, 0, componentWidth, componentHeight);
      doc.save(`${getMedicalDetails?.claimNumber}.pdf`);
    });
  };

  return (
    <div>
      {loader && <Loader />}
      <div className="row flex-lg-row flex-md-row flex-sm-column gap-4 align-items-lg-center align-items-md-center align-items-sm-start not-print mb-4">
        <div className="flex-1 col-lg-auto col-md-auto col-sm-12 col-12 ms-lg-2 ms-md-2 ms-4">
          <Button
            dataTestId="backToClaimList"
            onClick={backToMedicalClaims}
            variant="icon_btn"
            leftIcon={<RightArrowIcon />}
            className="rotate-180"
          >
            {t('claimsSummary.goback')}
          </Button>
        </div>
        <div className="col-md-auto col-lg-auto col-sm-12 col-12 text-center">
          <Button
            variant='secondary_btn'
            iconLeft={<PrintIcon />}
            onClick={Print}
            data-testid="printLink"
          >
            {t('common.print')}
          </Button>
        </div>
        <div className="col-md-auto col-lg-auto col-sm-12 col-12 text-center">
          <Button
            variant='secondary_btn'
            data-testid="downloadClaimsDetailsLink"
            leftIcon={<DownloadIcon color={darkgraycolor} />}
            onClick={DownloadPDF}
          >
            {t('common.download')}
          </Button>
        </div>
      </div>
      <div className="print-table print">
        <div className="mb-3">
          <Headings
            level={1}
            text={t('claimsSummary.claimDetails')}
            variant="semiBold"
            dataTestId="claimsDetailsHeader"
          />
        </div>
        <div className="print mb-4">
          {!showData && getMedicalDetails !== undefined && (
            <div className="table-responsive overflow-auto">
              <Table
                rows={[
                  [
                    {
                      text: (
                        <th scope='col' data-testid="memberNameLabel">
                          {t('common.memberNameWithColon')}
                        </th>
                      ),
                    },
                    {
                      text: (
                        <span data-testid="memberNameValue">
                          {getMedicalDetails?.memberName}
                        </span>
                      ),
                    },
                  ],
                  [
                    {
                      text: (
                        <th scope='col' data-testid="memberIdLabel">
                          {t('common.memberIdWithColon')}
                        </th>
                      ),
                    },
                    {
                      text: (
                        <span data-testid="memberIdValue">
                          {getMedicalDetails?.memberId}
                        </span>
                      ),
                    },
                  ],
                  [
                    {
                      text: (
                        <th scope='col' data-testid="claimNumberLabel">
                          {t('claimsSummary.claimNumber')}
                        </th>
                      ),
                    },
                    {
                      text: (
                        <span data-testid="claimNumberValue">
                          {getMedicalDetails?.claimNumber}
                        </span>
                      ),
                    },
                  ],

                  [
                    {
                      text: (
                        <th scope='col' data-testid="dateOfServiceLabel">
                          {t('claimsSummary.dateOfService')}
                        </th>
                      ),
                    },
                    {
                      text: (
                        <span data-testid="dateOfServiceValue">
                          {getMedicalDetails?.datesOfService &&
                            convertStringToDate(
                              getMedicalDetails?.datesOfService
                            )}
                        </span>
                      ),
                    },
                  ],
                  [
                    {
                      text: (
                        <th scope='col' data-testid="typeOfServiceLabel">
                          {t('claimsSummary.typeOfService')}
                        </th>
                      ),
                    },
                    {
                      text: (
                        <span data-testid="typeOfServiceValue">
                          {getMedicalDetails?.typesofService}
                        </span>
                      ),
                    },
                  ],
                  [
                    {
                      text: (
                        <th scope='col' data-testid="practiceNameLabel">
                          {t('claimsSummary.practiceName')}
                        </th>
                      ),
                    },
                    {
                      text: (
                        <span data-testid="practiceNameValue">
                          {getMedicalDetails?.practiceName}
                        </span>
                      ),
                    },
                  ],
                  [
                    {
                      text: (
                        <th scope='col' data-testid="provideNameLabel">
                          {t('claimsSummary.providerName')}
                        </th>
                      ),
                    },
                    {
                      text: (
                        <span data-testid="provideNameValue">
                          {getMedicalDetails?.providerName}
                        </span>
                      ),
                    },
                  ],

                  [
                    {
                      text: (
                        <th scope='col' data-testid="claimStatusLabel">
                          {t('claimsSummary.claimsStatus')}
                        </th>
                      ),
                    },
                    {
                      text: (
                        <span data-testid="claimStatusValue">
                          {getMedicalDetails?.claimsStatus}
                        </span>
                      ),
                    },
                  ],
                  [
                    {
                      text: (
                        <th scope='col' data-testid="totalChargesLabel">
                          {t('common.totalCharges')}
                        </th>
                      ),
                    },
                    {
                      text: (
                        <span data-testid="totalChargesValue">
                          {getMedicalDetails?.totalCharges &&
                            `$${getMedicalDetails?.totalCharges}`}
                        </span>
                      ),
                    },
                  ],
                  [
                    {
                      text: (
                        <th scope='col' data-testid="totalNotCoveredLabel">
                          {t('common.totalNotCovered')}
                        </th>
                      ),
                    },
                    {
                      text: (
                        <span data-testid="totalNotCoveredValue">
                          {getMedicalDetails?.tot_nv &&
                            `$${getMedicalDetails?.tot_nv}`}
                        </span>
                      ),
                    },
                  ],
                  [
                    {
                      text: (
                        <th scope='col' data-testid="totalCoveredLabel">
                          {t('common.totalCovered')}
                        </th>
                      ),
                    },
                    {
                      text: (
                        <span data-testid="totalCoveredValue">
                          {getMedicalDetails?.tot_cov &&
                            `$${getMedicalDetails?.tot_cov}`}
                        </span>
                      ),
                    },
                  ],
                  [
                    {
                      text: (
                        <th scope='col' data-testid="totalDeductibleLabel">
                          {t('common.totalDeductible')}
                        </th>
                      ),
                    },
                    {
                      text: (
                        <span data-testid="totalDeductibleValue">
                          {getMedicalDetails?.totalDeductible &&
                            `$${getMedicalDetails?.totalDeductible}`}
                        </span>
                      ),
                    },
                  ],
                  [
                    {
                      text: (
                        <th scope='col' data-testid="totalCopayCoinsuranceLabel">
                          {t('common.totalCopayCoinsurance')}
                        </th>
                      ),
                    },
                    {
                      text: (
                        <span data-testid="totalCopayCoinsuranceValue">
                          {getMedicalDetails?.tot_copay &&
                            `$${getMedicalDetails?.tot_copay}`}
                        </span>
                      ),
                    },
                  ],
                  [
                    {
                      text: (
                        <th scope='col' data-testid="totalPaidByPlanLabel">
                          {t('common.totalPaidByPlan')}
                        </th>
                      ),
                    },
                    {
                      text: (
                        <span data-testid="totalPaidByPlanValue">
                          {getMedicalDetails?.tot_paid &&
                            `$${getMedicalDetails?.tot_paid}`}
                        </span>
                      ),
                    },
                  ],
                  [
                    {
                      text: (
                        <th scope='col' data-testid="patientResponsibilityLabel">
                          {t('claimsSummary.patientResponsibility')}
                        </th>
                      ),
                    },
                    {
                      text: (
                        <span data-testid="patientResponsibilityValue">
                          {getMedicalDetails?.tot_resp &&
                            `$${getMedicalDetails?.tot_resp}`}
                        </span>
                      ),
                    },
                  ],
                  [
                    {
                      text: (
                        <th scope='col' data-testid="datePaymentSendToProviderLabel">
                          {t('claimsSummary.datePaymentSentToProvider')}
                        </th>
                      ),
                    },
                    {
                      text: (
                        <span data-testid="datePaymentSendToProviderValue">
                          {getMedicalDetails?.datePaymentSent &&
                            convertStringToDate(
                              getMedicalDetails?.datePaymentSent
                            )}
                        </span>
                      ),
                    },
                  ],
                  getFeature?.Eob &&
                    getMedicalDetails?.eob === 'true'
                    ? [
                      {
                        text: (
                          <ButtonLink
                            buttonText={t(
                              'claimsSummary.viewExplanationOfBenefits'
                            )}
                            dataTestId="downloadClaimsDetailsLink"
                            onClick={(e: any) => {
                              e.stopPropagation();
                              eobPDF(getMedicalDetails?.claimNumber);
                            }}
                          />
                        ),
                      },
                    ]
                    : null,
                ]}
              />
            </div>
          )}
          {/* To display the no content message when the data is empty */}
          {!showData && showNoContent && (
            <Paragraph
              text={t('common.noResults')}
              variant="regular"
              data-testid="noResults"
              className="ms-1"
            />
          )}
        </div>
        {getMedicalService && getMedicalService.length > 0 && (
          <>
            <div className="w-100 print" data-testid="typeofService">
              <Headings
                level={1}
                text={t('claimsSummary.typeofService')}
                className="mb-4"
                variant="semiBold"
              />
            </div>
            <div className="table-responsive overflow-auto">
              <Table
                className={' mb-4 print'}
                headerChildren={
                  <>
                    <th scope='col'>
                      <span
                        data-testid="authNumberTab"
                        className="visually-hidden"
                      >
                        {t('claimsSummary.amountDetails')}
                      </span>
                    </th>
                    {getMedicalService?.map((item: any, i: any) => {
                      return (
                        <th
                          scope='col'
                          data-testid="radiologyTab"
                          key={`medical-service-${item.serviceName}`}
                        >
                          {item.serviceName}
                        </th>
                      );
                    })}
                  </>
                }
                rowsChildren={
                  <>
                    <tr>
                      <td data-testid={`amountChargedLabel`}>
                        <strong>
                          {t('claimsSummary.amountCharged')}
                        </strong>
                      </td>
                      {getMedicalService &&
                        getMedicalService?.length > 0 &&
                        getMedicalService?.map((item: any, i: any) => {
                          return (
                            <td
                              key={`item${item?.amountCharged}`}
                              data-testid={`amountChargedValue`}
                            >
                              {item?.amountCharged && `$${item?.amountCharged}`}
                            </td>
                          );
                        })}
                    </tr>
                    <tr>
                      <td data-testid={`amountNotCoveredLabel`}>
                        <strong>
                          {t('claimsSummary.amountNotCovered')}
                        </strong>
                      </td>
                      {getMedicalService &&
                        getMedicalService?.length > 0 &&
                        getMedicalService?.map((item: any, i: any) => {
                          return (
                            <td
                              key={`item-${item?.amountNotCovered}`}
                              data-testid={`amountNotCoveredValue`}
                            >
                              {item?.amountNotCovered &&
                                `$${item?.amountNotCovered}`}
                            </td>
                          );
                        })}
                    </tr>
                    <tr>
                      <td data-testid={`amountAllowedLabel`}>
                        <strong>
                          {t('claimsSummary.amountAllowed')}
                        </strong>
                      </td>
                      {getMedicalService &&
                        getMedicalService?.length > 0 &&
                        getMedicalService?.map((item: any, i: any) => {
                          return (
                            <td
                              key={`item-${item?.amountAllowed}`}
                              data-testid={`amountAllowedValue`}
                            >
                              {item?.amountAllowed && `$${item?.amountAllowed}`}
                            </td>
                          );
                        })}
                    </tr>

                    <tr>
                      <td data-testid={`deductibleLabel`}>
                        <strong>{t('common.deductible')}</strong>
                      </td>
                      {getMedicalService &&
                        getMedicalService?.length > 0 &&
                        getMedicalService?.map((item: any, i: any) => {
                          return (
                            <td
                              key={`item-${item?.deductible}`}
                              data-testid={`deductibleValue`}
                            >
                              {item?.deductible && `$${item?.deductible}`}
                            </td>
                          );
                        })}
                    </tr>
                    <tr>
                      <td data-testid={`copayCoinsuranceLabel`}>
                        <strong>
                          {t('claimsSummary.copayCoinsurance')}
                        </strong>
                      </td>
                      {getMedicalService &&
                        getMedicalService?.length > 0 &&
                        getMedicalService?.map((item: any, i: any) => {
                          return (
                            <td
                              key={`item-${item?.copay}`}
                              data-testid={`copayCoinsuranceValue`}
                            >
                              {item?.copay && `$${item?.copay}`}
                            </td>
                          );
                        })}
                    </tr>
                    <tr>
                      <td data-testid={`amountPaidByPlanLabel`}>
                        <strong>
                          {t('claimsSummary.amountPaidByPlan')}
                        </strong>
                      </td>
                      {getMedicalService &&
                        getMedicalService?.length > 0 &&
                        getMedicalService?.map((item: any, i: any) => {
                          return (
                            <td
                              key={`item-${item?.amountPaidByPlan}`}
                              data-testid={`amountPaidByPlanValue`}
                            >
                              {item?.amountPaidByPlan &&
                                `$${item?.amountPaidByPlan}`}
                            </td>
                          );
                        })}
                    </tr>
                    <tr>
                      <td data-testid={`patientResponsibilityLabel`}>
                        <strong>
                          {t('claimsSummary.patientResponsibility')}
                        </strong>
                      </td>
                      {getMedicalService &&
                        getMedicalService?.length > 0 &&
                        getMedicalService?.map((item: any, i: any) => {
                          return (
                            <td
                              key={`item-${item?.patientResp}`}
                              data-testid={`patientResponsibilityValue`}
                            >
                              {item?.patientResp && `$${item?.patientResp}`}
                            </td>
                          );
                        })}
                    </tr>
                    <tr>
                      <td data-testid={`typeOfServiceNoteLabel`}>
                        <strong>{t('claimsSummary.note')}</strong>
                      </td>
                      {getMedicalService &&
                        getMedicalService?.length > 0 &&
                        getMedicalService?.map((item: any, i: any) => {
                          return (
                            <td
                              key={`item-${item?.noteCode}`}
                              data-testid={`typeOfServiceNoteValue`}
                            >
                              <OverlayTrigger
                                placement="top"
                                delay={{ show: 250, hide: 400 }}
                                overlay={
                                  <Tooltip
                                    data-testId="button_tooltip"
                                    className="button_tooltip"
                                    id="button_tooltip"
                                  >
                                    {item?.noteDesc}
                                  </Tooltip>
                                }
                              >
                                <span className="ws-nowrap text-decoration-underline">
                                  {' '}
                                  {item?.noteCode && `${item?.noteCode}`}
                                </span>
                              </OverlayTrigger>
                            </td>
                          );
                        })}
                    </tr>
                  </>
                }
              />
            </div>
          </>
        )}
      </div>
      <div className="disclaimer-text">
        <Paragraph
          text={t('claimsSummary.mouseOverNote')}
          variant="regular"
          data-testid="typeOfServiceDisclaimerText"
          className="mb-4"
        />
        <Paragraph
          text={
            <>
              {t('claimsSummary.claimsInstruction1')}{' '}
              {apiResponseSafeCheck(getPhone?.data?.benefitPhone1800) ? (
                <>
                  {t('common.questions')}{' '}

                  <Link
                    to={`tel:${getPhone?.data?.benefitPhone1800}`}
                    className="ws-nowrap text-decoration-underline px-1"
                  >
                    {getPhone?.data?.benefitPhone1800}
                    {t('common.dot')}
                  </Link>
                </>
              ) : null}
            </>
          }
          variant="regular"
          data-testid="claimsInstruction1"
          className="mb-4"
        />
        <Paragraph
          text={
            <>
              {t('medicalClaims.disclaimer5')}
              <Link
                to={`mailto:${t('medicalClaims.disclaimer6')}`}
                className="ws-nowrap text-decoration-underline px-1"
                data-testid="customerMailId"
              >
                {t('medicalClaims.disclaimer6')}
              </Link>
              {apiResponseSafeCheck(getPhone?.data) ? (
                <>
                  {t('medicalClaims.disclaimer7')}{' '}
                  <ContactInfo
                    phoneData={{
                      phone1800: getPhone?.data?.benefitPhone1800,
                      phoneLocal: getPhone?.data?.benefitPhoneLocal,
                    }}
                    className="ps-1"
                    dataTestId="contactInfo"
                  />
                </>
              ) : null}
            </>
          }
          data-testid="disclaimer5"
          className="mb-5"
        />
      </div>
    </div>
  );
};

export default MedicalClaimsDetails;
